import {
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormHelperText,
    FormLabel,
    Text,
    VStack
} from '@chakra-ui/react';
import InlineErrorState from '../../../components/states/InlineErrorState';
import InlineScopePrompt from '../../_shared/InlineScopePrompt';
import { translate } from '../../../i18n';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetCourts } from '../hooks/useGetCourts';
import { isUnreadable } from '../../analytics/utils/statValue';

interface SubCourtPickerProps {
    selectedSubCourtIds: number[];
    setSelectedSubCourtIds: (subCourtIds: number[]) => void;
    disabled?: boolean;
}

export default function SubCourtPicker({
    selectedSubCourtIds,
    setSelectedSubCourtIds,
    disabled = false
}: SubCourtPickerProps) {
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError, state } = useGetCourts(selectedLocation?.id);

    // Filter out composite courts - they shouldn't be selectable as sub-courts
    const availableCourts = courts?.filter(court => !court.isComposite) || [];

    const handleChange = (values: string[]) => {
        const numericIds = values.map(id => parseInt(id, 10));
        setSelectedSubCourtIds(numericIds);
    };

    // `state === 'loading'` rather than the raw `isLoading` (D62): React Query
    // v4 reports a DISABLED query as `isLoading: true` with
    // `fetchStatus: 'idle'`, so once run 134 gated this hook on `locationId` the
    // raw flag would have held this text on screen forever whenever no club was
    // selected — a permanent loading affordance, which D64 rates strictly worse
    // than the empty checkbox group it replaced.
    if (state === 'loading') {
        return <Text fontSize="sm" color="gray.500">{translate('loading')}</Text>;
    }

    // Before the empty branch, never after it: on a failed read `courts` is
    // undefined, so `availableCourts` is `[]` and the operator was told this
    // venue has no sub-courts to combine (R89-4 / D57).
    //
    // Paired with the data through `isUnreadable` (R132-4): React Query KEEPS
    // `courts` through a failed refetch, and `useUpdateCourt` invalidates this
    // key — so editing a court on a flaky connection took the whole checkbox
    // group away, along with the selection the operator had made in it.
    if (isUnreadable(isError, courts)) {
        return <InlineErrorState message={translate('courts.error_loading')} />;
    }

    // Never ISSUED: no club is selected, so "this venue has no sub-courts to
    // combine" is a claim about a question nobody asked (R110-6 / R133-4).
    if (state === 'never-issued') {
        return <InlineScopePrompt gate="location" />;
    }

    if (!availableCourts || availableCourts.length === 0) {
        return (
            <Text fontSize="sm" color="gray.500">
{translate('courts.no_available_subcourts')}
            </Text>
        );
    }

    return (
        <FormControl>
            <FormLabel>{translate('courts.select_subcourts')}</FormLabel>
            <FormHelperText mb={3} mt={-2}>
                {translate('courts.select_subcourts_helper')}
            </FormHelperText>

            <CheckboxGroup
                value={selectedSubCourtIds.map(id => id.toString())}
                onChange={handleChange}
            >
                <VStack align="start" spacing={2}>
                    {availableCourts.map((court: CourtDto) => (
                        <Checkbox
                            key={court.id}
                            value={court.id.toString()}
                            isDisabled={disabled}
                            size="md"
                        >
                            <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">{court.name}</Text>
                                {court.sports && court.sports.length > 0 && (
                                    <Text fontSize="xs" color="gray.500">
                                        {court.sports.join(', ')}
                                    </Text>
                                )}
                            </VStack>
                        </Checkbox>
                    ))}
                </VStack>
            </CheckboxGroup>

            {selectedSubCourtIds.length > 0 && (
                <Text mt={2} fontSize="sm" color="green.600">
                    {selectedSubCourtIds.length} {translate('courts.courts_selected' as any)}
                </Text>
            )}
        </FormControl>
    );
}
